/*
 * Copyright (c) 2017 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschuetzt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulaessig. Dies gilt
 * insbesondere fuer Vervielfaeltigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht uebergeben ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 *
 */
import {IComponentOptions} from 'angular';
import moment from 'moment';
import {OkHtmlDialogController} from '../../../core/dialog/OkHtmlDialogController';
import {DvDialog} from '../../../core/directive/dv-dialog/dv-dialog';
import RoleUtilService from '../../../core/service/RoleUtilService';
import {TSDocumentType} from '../../../models/enums/TSDokumentType';
import {
    eigenleistungPrefix,
    getEigenleistungsAnsatz,
    getEigenleistungsArtValues,
    TSEigenleistungArt,
} from '../../../models/enums/TSEigenleistungArt';
import {TSErfassungsArt} from '../../../models/enums/TSErfassungsArt';
import {TSSchadenfallStatus} from '../../../models/enums/TSSchadenfallStatus';
import TSEigenleistung from '../../../models/TSEigenleistung';
import TSEigenleistungKubikmeterDaten from '../../../models/TSEigenleistungKubikmeterDaten';
import TSEigenleistungLiterDaten from '../../../models/TSEigenleistungLiterDaten';
import TSEigenleistungStundenDaten from '../../../models/TSEigenleistungStundenDaten';
import TSSchadenfall from '../../../models/TSSchadenfall';
import IFormController = angular.IFormController;
import ILogService = angular.ILogService;
import ITranslateService = angular.translate.ITranslateService;

declare let require: any;
declare let angular: any;
let styles = require('./dv-eigenleistungen.scss');
let okHtmlDialogTemplate = require('../../../core/dialog/okHtmlDialogTemplate.html');

export class DVEigenleistungenComponentConfig implements IComponentOptions {
    transclude = false;
    bindings: any = {
        eigenleistung: '=',
        name: '@',
        readonly: '<',
        schadenfallStatus: '<',
        schadenfall: '<',
        saveDisabled: '=',
    };
    template = require('./dv-eigenleistungen.html');

    controller = DVEigenleistungenController;
    controllerAs = 'vm';
    require: any = {parentForm: '?^form'};
}

class TSEigenleistungArtWithFlag {
    art?: TSEigenleistungArt;
    disabled: boolean;

    constructor(art?: TSEigenleistungArt) {
        this.art = art;
        this.disabled = false;
    }

}

export class DVEigenleistungenController {

    eigenleistung?: TSEigenleistung;
    name: string;
    readonly: boolean;
    MIN_AGE = 13;
    parentForm: IFormController;
    halfHourValues: Array<any>;
    arbeitsstunden: number | undefined;
    kubikmeter: number | undefined;
    liter: number | undefined;
    datum: moment.Moment | undefined;
    schadenfallStatus: TSSchadenfallStatus;
    schadenfall: TSSchadenfall;
    saveDisabled: boolean;
    savedHandarbeiters: Array<string>;
    savedEntries: Array<TSEigenleistungArt>;
    selectableLeistungsarten: Array<TSEigenleistungArtWithFlag>;
    betragForAndere: number;
    stundenansatz: number;
    doctype: TSDocumentType = TSDocumentType.EIGENLEISTUNG_DOC;

    static $inject: any[] = ['CONSTANTS', '$log', 'RoleUtilService', '$translate', 'DvDialog'];

    /* @ngInject */
    constructor(private CONSTANTS: any, private $log: ILogService, private roleUtilService: RoleUtilService,
                private $translate: ITranslateService, private dvDialog: DvDialog,
    ) {
        this.$log.debug('initializing eigenleistung componente');

    }

    $onInit() {
        if (this.eigenleistung === undefined || this.eigenleistung === null) {
            this.$log.error('"eigenleistung" attribute is required for this component')
        }
        this.halfHourValues = this.initHalfHourValues();

        if (this.eigenleistung && this.eigenleistung.eigenleistungKubikmeterDaten) {
            this.kubikmeter = this.eigenleistung.eigenleistungKubikmeterDaten.kubikmeter;
        }

        if (this.eigenleistung && this.eigenleistung.eigenleistungLiterDaten) {
            this.liter = this.eigenleistung.eigenleistungLiterDaten.liter;
        }

        if (this.isHandarbeitPauschal() || this.isMaschinenarbeitPauschal()) {
            this.setStundenansatz();
        }

        let relevanteEigenleistungen = this.schadenfall.getRelevanteEigenleistungen(this.isSchaetzung());
        //  this.vorname + ' ' + this.name + ' (' + this.jahrgang + ')'
        this.savedHandarbeiters = relevanteEigenleistungen
            .filter(eigen => eigen.eigenleistungDatenFS.eigenleistungArt === TSEigenleistungArt.HANDARBEIT)
            .map((eigenleistung) => eigenleistung.eigenleistungDatenFS.displayName);

        this.savedEntries = relevanteEigenleistungen
            .filter(eigen => eigen.eigenleistungDatenFS.eigenleistungArt !== TSEigenleistungArt.HANDARBEIT)
            .map((eigenleistung) => eigenleistung.eigenleistungDatenFS.eigenleistungArt);

        this.initSelectableLeistungsarten();
    }

    public getModel(): TSEigenleistung | undefined {
        return this.eigenleistung;
    }

    public getTSLeistungsartenValues(): Array<TSEigenleistungArt> {
        let eigenleistung = this.getModel();
        const schadendatum = (this.schadenfall &&
                              this.schadenfall.schaeden &&
                              this.schadenfall.schaeden.length > 0 &&
                              this.schadenfall.schaeden[0] &&
                              this.schadenfall.schaeden[0].schadenDatenFS) ?
                              this.schadenfall.schaeden[0].schadenDatenFS.schadendatum : undefined;
        let values = getEigenleistungsArtValues(schadendatum);
        if (this.schadenfall.erfassungsart === TSErfassungsArt.ONLINE && !this.isSchaetzung()) {
            this.removeHandarbeitPauschalElement(values);
        }

        if (eigenleistung && eigenleistung.eigenleistungDatenFS.eigenleistungArt) {
            const currentArt = eigenleistung.eigenleistungDatenFS.eigenleistungArt;
            if (!values.includes(currentArt)) {
                values.push(currentArt);
            }
        }

        return values;
    }

    private removeHandarbeitPauschalElement(values: Array<TSEigenleistungArt>) {
        values.splice(values.indexOf(TSEigenleistungArt.HANDARBEIT_PAUSCHAL), 1);
    }

    public initSelectableLeistungsarten(): void {
        let result = new Array<TSEigenleistungArtWithFlag>();
        if (this.isSchaetzung()) {
            this.getSchaetzungLeistungsarten(result);
        } else {
            this.getActualLeistungsarten(result);
        }

        this.selectableLeistungsarten = result;
    }

    private getSchaetzungLeistungsarten(result: TSEigenleistungArtWithFlag[]) {
        result.push(new TSEigenleistungArtWithFlag(TSEigenleistungArt.HANDARBEIT_PAUSCHAL));
        result.push(new TSEigenleistungArtWithFlag(TSEigenleistungArt.HOLZ));
        result.push(new TSEigenleistungArtWithFlag(TSEigenleistungArt.ANDERE));
    }

    private getActualLeistungsarten(result: TSEigenleistungArtWithFlag[]) {
        if (!this.eigenleistung) {
            return;
        }
        const editedEntryArt = this.eigenleistung.eigenleistungDatenFS.eigenleistungArt;
        this.getTSLeistungsartenValues().forEach(value => {
            let elemWithFlag = new TSEigenleistungArtWithFlag();
            elemWithFlag.art = value;
            elemWithFlag.disabled = this.savedEntries.includes(value) && value !== editedEntryArt;
            result.push(elemWithFlag)
        });
    }

    public getJahrgangValues(): Array<number> {
        let allowedJahrgaenge: Array<number> = [];
        let maxJahrgang = new Date().getFullYear() - this.MIN_AGE;
        for (let i = 1900; i <= maxJahrgang; i++) {
            allowedJahrgaenge.push(i);
        }
        allowedJahrgaenge.reverse();
        return allowedJahrgaenge;
    }

    public translateEigenleistung(art: TSEigenleistungArt): string {
        let prefix = eigenleistungPrefix();
        return this.$translate.instant(prefix + TSEigenleistungArt[art])
    }

    public changedEigenleistungsart() {
        this.parentForm.$setPristine();
        this.parentForm.$setUntouched();

        if (this.eigenleistung) {
            //empty array
            this.eigenleistung.eigenleistungStundenDaten.splice(0);
        }

        this.initKubikmeter();
        this.initLiter();
        this.initPauschaleLeistungsarten();
    }

    private initKubikmeter() {
        if (this.eigenleistung) {
            if (this.showKubikmeter()) {
                this.eigenleistung.eigenleistungKubikmeterDaten = new TSEigenleistungKubikmeterDaten();
                this.eigenleistung.eigenleistungStundenDaten = [];
            } else {
                this.eigenleistung.eigenleistungKubikmeterDaten = undefined;
            }
        }
    }

    private initLiter() {
        if (this.eigenleistung) {
            if (this.showLiter()) {
                this.eigenleistung.eigenleistungLiterDaten = new TSEigenleistungLiterDaten();
                this.eigenleistung.eigenleistungStundenDaten = [];
            } else {
                this.eigenleistung.eigenleistungLiterDaten = undefined;
            }
        }
    }

    private initPauschaleLeistungsarten() {
        if (this.isHandarbeitPauschal() || this.isMaschinenarbeitPauschal()) {
            let eigenleistung = this.getModel();
            if (eigenleistung && eigenleistung.eigenleistungDatenFS) {
                // StundenDaten will not be added with the button, but manually
                // (only one StundenDaten entry is made for handarbeit pauschal)
                let eigenleistungStundenDaten = new TSEigenleistungStundenDaten();
                eigenleistungStundenDaten.datum = moment();
                eigenleistung.eigenleistungStundenDaten.push(eigenleistungStundenDaten);
                this.setStundenansatz();
            }
        }
    }

    private setStundenansatz() {
        if (this.isHandarbeitPauschal()) {
            this.stundenansatz = getEigenleistungsAnsatz(TSEigenleistungArt.HANDARBEIT, this.schadenfall.schaeden[0].schadenDatenFS.schadendatum);
        } else {
            this.stundenansatz = 0;
        }
    }

    public calculateBetrag(inputValue: number): number {
        let eigenleistung = this.getModel();
        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt) {
            let ansatz = this.isAndereLeistungArt() ? this.stundenansatz :
                getEigenleistungsAnsatz(eigenleistung.eigenleistungDatenFS.eigenleistungArt, this.schadenfall.schaeden[0].schadenDatenFS.schadendatum);

            if (!isNaN(inputValue)) {
                return inputValue * ansatz;
            }
        }
        return 0;
    }

    public addStunden(): void {
        if (this.isHandarbeit() && this.isDuplicatedHandarbeiter()) {
            this.dvDialog.showDialog(okHtmlDialogTemplate, OkHtmlDialogController, {
                title: 'TITLE_DUPLICATED_HANDARBEITER',
                text: 'DUPLICATED_HANDARBEITER_MESSAGE',
            });
            this.saveDisabled = true;
            return;
        }
        this.saveDisabled = false;

        let eigenleistung = this.getModel();
        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt && this.datum && this.arbeitsstunden) {
            let newStunden = new TSEigenleistungStundenDaten();
            newStunden.datum = this.datum;
            newStunden.arbeitsstunden = this.arbeitsstunden;
            newStunden.betrag = this.calculateBetrag(this.arbeitsstunden);
            this.datum = undefined;
            this.arbeitsstunden = undefined;
            this.betragForAndere = 0;
            eigenleistung.eigenleistungStundenDaten.push(newStunden);
        }
    }

    private getBetragHandarbeitPauschal() {
        let eigenleistung = this.getModel();
        if (eigenleistung) {
            return eigenleistung.eigenleistungStundenDaten[0].betrag = eigenleistung.eigenleistungStundenDaten[0].arbeitsstunden * this.stundenansatz;
        }
        return 0;
    }

    private isDuplicatedHandarbeiter(): boolean {
        //if id is defined, then this person is already saved => we are in edit mode
        if (!this.eigenleistung) {
            return false;
        }
        const editMode: boolean = !!this.eigenleistung.id;

        // it is not a duplicate if we are in edit mode
        if (editMode) {
            return false;
        }

        //  this.vorname + ' ' + this.name + ' (' + this.jahrgang + ')'
        const actualArbeiter = this.eigenleistung.eigenleistungDatenFS;
        const actualArbeiterName = actualArbeiter.vorname + ' ' + actualArbeiter.name + ' (' + actualArbeiter.jahrgang + ')';

        return this.savedHandarbeiters.some(handarbeiter => actualArbeiterName === handarbeiter);
    }

    public addKubikmeter(): void {
        let eigenleistung = this.getModel();

        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt && this.kubikmeter) {
            let newKubikmeter = new TSEigenleistungKubikmeterDaten();
            newKubikmeter.kubikmeter = this.kubikmeter;
            newKubikmeter.betrag = this.calculateBetrag(this.kubikmeter);
            this.datum = undefined;
            eigenleistung.eigenleistungKubikmeterDaten = newKubikmeter;
        }
    }

    public addLiter(): void {
        let eigenleistung = this.getModel();

        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt && this.liter) {
            let newLiter = new TSEigenleistungLiterDaten();
            newLiter.liter = this.liter;
            newLiter.betrag = this.calculateBetrag(this.liter);
            this.datum = undefined;
            eigenleistung.eigenleistungLiterDaten = newLiter;
        }
    }

    public showPersonendaten(): boolean {
        let eigenleistung = this.getModel();
        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt) {
            return eigenleistung.eigenleistungDatenFS.eigenleistungArt === TSEigenleistungArt.HANDARBEIT;
        }
        return false;

    }

    public initHalfHourValues(): Array<any> {
        let items: any = [];
        for (let i = 0; i <= 20; i++) {
            let numVal = i * 0.5;
            let halfstring = i % 2 === 0 ? '0' : '5';
            let label = '' + Math.floor(i / 2) + '.' + halfstring + 'h';
            items.push({label: label, value: numVal});

        }
        return items;

    }

    public getPrefix(): string {
        return eigenleistungPrefix();
    }

    public calcTotalELHours(): number {
        let total = 0;
        if (this.eigenleistung !== undefined) {
            return this.eigenleistung.getArbeitsstundenTotal();
        }
        return total;
    }

    public calcTotalELBetrag(): number {
        let total = 0;
        if (this.eigenleistung !== undefined) {
            return this.eigenleistung.getBetragTotal();
        }
        return total;
    }

    public deleteClicked(eigenleistungDatenToDelete: any, event: any, index: any) {
        let eigenleistung = this.getModel();
        if (eigenleistung) {
            if (!this.markAsdeleteMode(eigenleistungDatenToDelete)) {
                if (index > -1) {
                    if (eigenleistungDatenToDelete instanceof TSEigenleistungStundenDaten) {
                        eigenleistung.eigenleistungStundenDaten.splice(index, 1); //remove from list
                    }
                }
            } else {
                eigenleistungDatenToDelete.deleted = true; //only mark as deleted
            }
        }
    }

    public markAsdeleteMode(eigenleistungDatenToDelete: any): boolean {
        if (this.schadenfallStatus === TSSchadenfallStatus.WIEDERHERSTELLUNG) {
            // immer direckt löschen wenn Status = WIEDERHERSTELLUNG
            return false;
        } else if (eigenleistungDatenToDelete.timestampErstellt === undefined) {
            // Noch nicht gespeicherte daten können gelöscht werden.
            return false;
        }
        // ansonsten markieren wir nur als gelöscht
        return true;
    }

    public showKubikmeter(): boolean {
        let eigenleistung = this.getModel();

        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt) {
            return eigenleistung.eigenleistungDatenFS.eigenleistungArt === TSEigenleistungArt.HOLZ ||
                eigenleistung.eigenleistungDatenFS.eigenleistungArt === TSEigenleistungArt.HOLZ_AUF_PLATZ;
        }
        return false;
    }

    public showLiter(): boolean {
        let eigenleistung = this.getModel();

        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt) {
            return eigenleistung.eigenleistungDatenFS.eigenleistungArt === TSEigenleistungArt.TREIBSTOFF;
        }
        return false;
    }

    public isAndereLeistungArt(): boolean {
        let eigenleistung = this.getModel();

        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt) {
            return eigenleistung.eigenleistungDatenFS.eigenleistungArt === TSEigenleistungArt.ANDERE;
        }
        return false;
    }

    public isHandarbeit(): boolean {
        let eigenleistung = this.getModel();

        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt) {
            return eigenleistung.eigenleistungDatenFS.eigenleistungArt === TSEigenleistungArt.HANDARBEIT;
        }
        return false;
    }

    public isHandarbeitPauschal(): boolean {
        let eigenleistung = this.getModel();

        if (eigenleistung && eigenleistung.eigenleistungDatenFS && eigenleistung.eigenleistungDatenFS.eigenleistungArt) {
            return eigenleistung.eigenleistungDatenFS.eigenleistungArt === TSEigenleistungArt.HANDARBEIT_PAUSCHAL;
        }
        return false;
    }

    public getRequiredDocs(): number {
        if (this.isSchaetzung()) {
            return 0;
        }
        return 1;
    }

    private isSchaetzung(): boolean {
        let eigenleistung = this.getModel();
        return !!(eigenleistung && eigenleistung.schaetzung);

    }

    private isMaschinenarbeitPauschal(): boolean {
        return this.isAndereLeistungArt() && this.isSchaetzung();
    }
}




// WEBPACK FOOTER //
// ./src/app/gesuch/component/dv-eigenleistungen/dv-eigenleistungen.ts